import styled from '@emotion/styled';
import { ListPageShell, ListTabs, ListTypeTab } from './ListPageLayout';
import PageHeader from './PageHeader';
import { claudeTokens } from '@/styles/claudeTokens';
import { ComponentProps } from 'react';
import { FormSectionTabs } from '@/components/common/FormNavigation';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';

/** 마이페이지에서 이어지는 목록·내역 화면. 목록 공통 폭과 홈의 평평한 흰 면을 재사용한다. */
export const AccountPageShell = styled(ListPageShell)`
    padding-top: 16px;
    padding-bottom: 24px;
    color: ${claudeTokens.color.text1};
    word-break: keep-all;
    overflow-wrap: anywhere;
    text-wrap: pretty;
    /* ListPageShell의 모바일 padding 축약 규칙 뒤에도 제목 위 여백을 유지한다. */
    @media (max-width: 768px) { padding-top: 16px; padding-bottom: 24px; }
    /* 앱 본문의 좌우 여백은 MUI md(900px)부터 24px이다. */
    @media (min-width: 769px) and (max-width: 899px) {
        width: calc(100% + 16px);
        margin-left: -8px;
        margin-right: -8px;
    }
`;

/** 앱 헤더가 이동을 담당한다. 본문 제목에는 중복 뒤로가기 props를 받지 않는다. */
type AccountPageHeaderProps = Omit<ComponentProps<typeof PageHeader>, 'backLabel' | 'backTo' | 'onBack'>;
const AccountHeading = (props: AccountPageHeaderProps) => <PageHeader {...props} />;

export const AccountPageHeader = styled(AccountHeading)`
    h1 { font-size: 24px; line-height: 1.4; color: ${claudeTokens.color.text1}; }
    @media (max-width: 768px) { h1 { font-size: 22px; } }
`;

/** 제목 → 탭 → 본문 → 저장 바를 하나의 흰 면에 두는 긴 폼. */
export const AccountFormShell = styled(AccountPageShell)`
    min-width: 0;
    padding-bottom: 0;
    @media (max-width: 768px) { padding-bottom: 0; }
`;
export const AccountFormBody = styled.div`min-width: 0; padding: 24px 0;`;
export const AccountFormTabs = styled(FormSectionTabs)`
    && { margin: 0; padding: 0; border-bottom-color: ${FORM_COLOR.line}; }
    > button:first-of-type { padding-left: 0; }
`;

/** 기존 폼의 섹션 탭·저장 바는 바깥에 두어 sticky 동작을 보존한다. */
export const AccountFormPanel = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    margin: 16px 0 24px;
    padding: 16px;
    background: ${claudeTokens.color.surface};
    border-radius: ${claudeTokens.radius.md};
    word-break: keep-all;
    overflow-wrap: anywhere;
    text-wrap: pretty;
    @media (max-width: 768px) { margin: 8px 0 16px; padding: 16px 8px; }
`;

export const AccountHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
    @media (max-width: 768px) { margin-bottom: 16px; }
`;

export const AccountTitle = styled.h1`
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.4;
    color: ${claudeTokens.color.text1};
    word-break: keep-all;
    @media (max-width: 768px) { font-size: 22px; }
`;

export const AccountSubtitle = styled.p`
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${claudeTokens.color.text2};
    word-break: keep-all;
`;

export const AccountSectionTitle = styled.h2`
    margin: 0 0 16px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
    color: ${claudeTokens.color.text1};
    @media (max-width: 768px) { font-size: 18px; }
`;

export const AccountFormContent = styled.div`min-width: 0;`;
/** 긴 폼 섹션 사이 구분선 — UI 가이드 1장 구분선 #E3E8E6(FORM_COLOR.line). 예전 베이지(#E8E0D5)는 폼 칸 테두리와 색이 달랐다 */
export const AccountDivider = styled.div`
    height: 1px;
    margin: 24px 0;
    background: ${FORM_COLOR.line};
`;

export const AccountTabs = styled(ListTabs)`
    border-bottom: 1px solid ${claudeTokens.color.border};
    margin-bottom: 20px;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`;
export const AccountTab = styled(ListTypeTab)`
    @media (max-width: 768px) { flex: 1 0 auto; padding: 13px 12px; font-size: 14px; }
`;

/** 빈 목록 문구 — 공용 빈 상태(PageState)와 같은 15px 흐림(#66736F). 다음 행동 버튼이 필요하면 PageState 를 쓴다 */
export const AccountEmpty = styled.div`
    padding: 48px 16px;
    text-align: center;
    font-size: 15px;
    line-height: 1.6;
    color: ${FORM_COLOR.muted};
    word-break: keep-all;
`;
